import WindowControl from '../components/ui/WindowControl';
import { createContext, useContext, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Button } from '../components/ui';
import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react';

const DialogContext = createContext();

export const useDialog = () => useContext(DialogContext);

export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [ackChecked, setAckChecked] = useState(false);

  const showAlert = useCallback((message, { title, type = 'info', align } = {}) => {
    return new Promise((resolve) => {
      setAckChecked(false);
      setDialog({ message, title, type, mode: 'alert', align, resolve });
    });
  }, []);

  const showConfirm = useCallback((message, { title, type = 'warning', confirmText = 'Bestätigen', cancelText = 'Abbrechen', align } = {}) => {
    return new Promise((resolve) => {
      setAckChecked(false);
      setDialog({ message, title, type, mode: 'confirm', confirmText, cancelText, align, resolve });
    });
  }, []);

  /**
   * Mehr-Wege-Auswahl: Zeigt mehrere Aktions-Buttons an und gibt den `key`
   * des gewählten Buttons zurück (oder `null` bei Abbruch).
   *
   * Optional: `requireAck` — wenn gesetzt, erscheint eine Checkbox; alle
   * `danger`-Buttons sind erst nach dem Anhaken aktivierbar (rechtssichere
   * Force-Override z. B. für GoBD-Soft-Lock).
   */
  const showChoice = useCallback((message, { title, type = 'info', choices = [], align, cancelText = 'Abbrechen', requireAck = null } = {}) => {
    return new Promise((resolve) => {
      setAckChecked(false);
      setDialog({ message, title, type, mode: 'choice', choices, cancelText, align, requireAck, resolve });
    });
  }, []);

  const closeDialog = useCallback((result) => {
    if (dialog?.resolve) dialog.resolve(result);
    setDialog(null);
  }, [dialog]);

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />;
      case 'error': return <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />;
      default: return <Info className="w-5 h-5 text-yellow-400 flex-shrink-0" />;
    }
  };

  const getDefaultTitle = (type) => {
    switch (type) {
      case 'success': return 'Erfolgreich';
      case 'warning': return 'Achtung';
      case 'error': return 'Fehler';
      default: return 'Hinweis';
    }
  };

  return (
    <DialogContext.Provider value={{ showAlert, showConfirm, showChoice }}>
      {children}
      {dialog && ReactDOM.createPortal(
        <div className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 px-4 ${dialog.align === 'left' ? 'lg:pr-[25vw]' : ''}`} style={{ zIndex: 2147483647 }} onClick={() => closeDialog(dialog.mode === 'choice' ? null : false)}>
          <div className="bg-gray-800 border border-yellow-400 rounded-xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {getIcon(dialog.type)}
                <h3 className="text-base font-semibold text-yellow-400">{dialog.title || getDefaultTitle(dialog.type)}</h3>
              </div>
              <WindowControl
                onClick={() => closeDialog(dialog.mode === 'choice' ? null : false)}
                
                aria-label="Schließen"
              >
                <X className="w-6 h-6" strokeWidth={2.5} />
              </WindowControl>
            </div>
            <p className="text-gray-300 text-sm mb-5 whitespace-pre-line">{dialog.message}</p>
            {dialog.mode === 'choice' && dialog.requireAck && (
              <label className="flex items-start gap-2 mb-4 p-3 bg-yellow-50 dark:bg-gray-900/60 border border-yellow-500 dark:border-yellow-600/40 rounded-md cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={ackChecked}
                  onChange={(e) => setAckChecked(e.target.checked)}
                  data-testid="gobd-ack-checkbox"
                  className="mt-0.5 w-4 h-4 accent-yellow-400 cursor-pointer"
                />
                <span className="text-xs text-gray-800 dark:text-gray-200 leading-snug">{dialog.requireAck}</span>
              </label>
            )}
            {dialog.mode === 'alert' ? (
              <Button
                onClick={() => closeDialog(true)}
                className="w-full bg-yellow-400 text-black hover:bg-yellow-500 font-medium min-h-9 py-2 border-2 border-yellow-400"
              >
                Verstanden
              </Button>
            ) : dialog.mode === 'choice' ? (
              <div className="flex flex-col gap-2">
                {/* Primary (Annehmen) – volle Breite, zuerst */}
                {(dialog.choices || []).filter((c) => c.variant !== 'danger').map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => closeDialog(c.key)}
                    data-testid={c.testid}
                    ref={(el) => {
                      if (!el) return;
                      el.style.setProperty('background', '#facc15', 'important');
                      el.style.setProperty('background-image', 'none', 'important');
                      el.style.setProperty('border', '2px solid #facc15', 'important');
                      el.style.setProperty('color', '#000000', 'important');
                    }}
                    className="w-full inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors min-h-9 py-2 px-4"
                  >
                    {c.label}
                  </button>
                ))}
                {/* Danger + Cancel nebeneinander */}
                {(dialog.choices || []).some((c) => c.variant === 'danger') && (
                  <div className="flex gap-2">
                    {(dialog.choices || []).filter((c) => c.variant === 'danger').map((c) => {
                      const disabled = !!dialog.requireAck && !ackChecked;
                      return (
                      <button
                        key={c.key}
                        type="button"
                        disabled={disabled}
                        onClick={() => !disabled && closeDialog(c.key)}
                        data-testid={c.testid}
                        ref={(el) => {
                          if (!el) return;
                          el.style.setProperty('background', 'transparent', 'important');
                          el.style.setProperty('background-image', 'none', 'important');
                          el.style.setProperty('border', `2px solid ${disabled ? '#7f1d1d' : '#ef4444'}`, 'important');
                          el.style.setProperty('color', disabled ? '#7f1d1d' : '#fca5a5', 'important');
                          el.style.setProperty('box-shadow', 'none', 'important');
                          el.style.setProperty('cursor', disabled ? 'not-allowed' : 'pointer', 'important');
                          el.style.setProperty('opacity', disabled ? '0.55' : '1', 'important');
                        }}
                        className="flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors min-h-9 py-2 px-4"
                      >
                        {c.label}
                      </button>
                      );
                    })}
                    <Button
                      variant="outline"
                      onClick={() => closeDialog(null)}
                      className="flex-1 min-h-9 py-2 whitespace-normal leading-tight text-sm"
                    >
                      {dialog.cancelText}
                    </Button>
                  </div>
                )}
                {/* Falls keine Danger-Option vorhanden ist, Cancel als Vollbreiten-Button */}
                {!(dialog.choices || []).some((c) => c.variant === 'danger') && (
                  <Button
                    variant="outline"
                    onClick={() => closeDialog(null)}
                    className="w-full min-h-9 py-2 whitespace-normal leading-tight text-sm"
                  >
                    {dialog.cancelText}
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => closeDialog(false)}
                  className="flex-1 min-h-9 py-2 whitespace-normal leading-tight text-sm"
                >
                  {dialog.cancelText}
                </Button>
                <Button
                  onClick={() => closeDialog(true)}
                  className="flex-1 bg-yellow-400 text-black hover:bg-yellow-500 font-medium min-h-9 py-2 whitespace-normal leading-tight text-sm border-2 border-yellow-400"
                >
                  {dialog.confirmText}
                </Button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </DialogContext.Provider>
  );
};
